import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as AnalystService from 'services/agents/analyst';

export const useAnalystService = () => {
  const { token } = useSelector((store) => store.user_data);

  const fetchTenantReport = async (tenant_id, options = {}) => {
    const { error, payload } = await AnalystService.fetchTenantReport(tenant_id, {
      ...options,
      token
    });
    if (error) {
      toast.error(error);
      return { report: null };
    }
    return { report: payload };
  };

  const fetchDashboard = async (options = {}) => {
    const { error, payload } = await AnalystService.fetchDashboard({ ...options, token });
    if (error) {
      toast.error(error);
      return { dashboard: null };
    }
    return { dashboard: payload };
  };

  const fetchTenants = async (options = {}) => {
    const { error, payload } = await AnalystService.fetchTenants({ ...options, token });
    if (error) {
      toast.error(error);
      return { tenants: [], size: 0 };
    }
    const {
      data: tenants,
      meta: { size }
    } = payload;
    return { tenants, size };
  };

  const fetchAnomalies = async (options = {}) => {
    const { error, payload } = await AnalystService.fetchAnomalies({ ...options, token });
    if (error) {
      toast.error(error);
      return { anomalies: [], size: 0 };
    }
    const {
      data: anomalies,
      meta: { size }
    } = payload;
    return { anomalies, size };
  };

  const fetchReports = async (options = {}) => {
    const { error, payload } = await AnalystService.fetchReports({ ...options, token });
    if (error) {
      toast.error(error);
      return { reports: [], size: 0 };
    }
    const {
      data: reports,
      meta: { size }
    } = payload;
    return { reports, size };
  };

  const fetchReport = async (report_id, options = {}) => {
    const { error, payload: report } = await AnalystService.fetchReport(report_id, {
      ...options,
      token
    });
    if (error) {
      toast.error(error);
      return { report: null };
    }
    return { report };
  };

  const actionRecommendation = async (report_id, index, actioned, options = {}) => {
    const { error } = await AnalystService.actionRecommendation(report_id, index, actioned, {
      ...options,
      token
    });
    if (error) {
      toast.error(error || 'Unable to update recommendation.');
      return false;
    }
    return true;
  };

  const fetchMonthlyReports = async (options = {}) => {
    const { error, payload } = await AnalystService.fetchMonthlyReports({ ...options, token });
    if (error) {
      toast.error(error);
      return { reports: [], size: 0 };
    }
    const {
      data: reports,
      meta: { size }
    } = payload;
    return { reports, size };
  };

  const fetchMonthlyReport = async (year, month, options = {}) => {
    const { error, payload: report } = await AnalystService.fetchMonthlyReport(year, month, {
      ...options,
      token
    });
    if (error) {
      toast.error(error);
      return { report: null };
    }
    return { report };
  };

  const triggerRun = async (scope = 'all', options = {}) => {
    const { error } = await AnalystService.triggerRun({ ...options, data: { scope }, token });
    if (error) {
      toast.error(error || 'Unable to trigger analysis run.');
      return false;
    }
    toast.success('Analysis run started');
    return true;
  };

  return {
    fetchDashboard,
    fetchTenantReport,
    fetchTenants,
    fetchAnomalies,
    fetchReports,
    fetchReport,
    fetchMonthlyReports,
    fetchMonthlyReport,
    actionRecommendation,
    triggerRun
  };
};
